import { ApolloError, useMutation, useQuery } from "@apollo/client";
import {
    getAllMillData,
    getMillSettingsData,
    upsertMillShiftsKSFCrewsData,
    insertMillWithAdminUser,
    getMillShifts,
    isOnboardingCorporateBackoffice,
    enableOrDisableMill,
    getEventFailureCodeSummaryPerMCQuery,
    getEventFunctionalLocationSummaryPerMCQuery,
    getMillMaximumOvertime,
    getMillCountryCode,
    upsertMillWithAdminUser,
    getMillTimezone,
    updateMillFeatureCluster,
    updateMillDedicatedLocalserver,
    upsertMillModule,
} from "api/queries/MillsAPI";
import {
    IDay,
    IKeySuccessFactors,
    IMeasureMetric,
} from "pages/Corporate/CorporateSetupDashboard/Corporations/MillSettingsStep/MillSettingsStepAPIInterfaces";
import {
    IAllMillData,
    IMillSettingsEntity,
    IShift,
    IEventFailureCodeSummaryPerMC,
    IEventFunctionalLocationSummaryPerMC,
    IDowntimeSummaryByFailureTypeTotal,
    IDowntimeSummaryByFailureCodeTotal,
    IDowntimeSummaryTotalsByTypePerMachineCenter,
    IDowntimeSummaryTotalsByCodePerMachineCenter,
    IDowntimeUptimeSummaryTotalsByProductionLine,
    IDowntimeSummaryByFunctionalLocationTotal,
    I13WeekTrendFilters,
    I13WeekTrendDowntimeTotals,
    IDowntimeSummaryBySecondaryFailureCodeTotal,
    IOeeSummaryQueryFilters,
} from "./MillsEntityInterfaces";
import { useCallback, useEffect, useMemo, useState } from "react";
import { IMillFilters } from "pages/Corporate/Locations/MillDashboards/SummaryReport/components/SummaryReportFilters/SummaryReportFiltersInterfaces";
import { END_OF_SHIFT_FAILURE_CODE_NAME, SummaryOption, allElements } from "constants/constants";
import { API } from "api/API";
import { getEndOfDateRange, getStartOfDateRange, isApiAdministrator } from "utilities/utilities";
import { IProductionDateRange } from "utilities/oeeDateRange";
import moment from "moment";
import {
    get13WeekDowntimeSummaryTotalsByFailureCode,
    get13WeekDowntimeSummaryTotalsBySecondaryFailureCode,
    getDowntimeSummaryByFailureCodeMillDashboardFiltered,
    getDowntimeSummaryByFailureTypeMillDashboard,
    getDowntimeSummaryBySecondaryFailureCodeMillDashboardFiltered,
    getDowntimeSummaryFailureCodeByMachineCenterMillDashboardFiltered,
    getDowntimeSummaryFailureTypePerMachineCenterMillDashboardFiltered,
    getDowntimeSummarySecondaryFailureCodeByMachineCenterMillDashboardFiltered,
    getDowntimeSummaryByFunctionalLocationMillDashboardFiltered,
    getUptimesVsDowntimesTotalsByProductionLineMillDashboard,
    IGetDowntimeSummaryByFailureTypeMillDashboardResponse,
    IGetDowntimeSummaryByFailureCodeMillDashboardResponse,
    IGetDowntimeSummaryBySecondaryFailureCodeMillDashboardResponse,
    IGetDowntimeSummaryFailureCodeByMachineCenterMillDashboardFilteredResponse,
    IGetDowntimeSummaryByFunctionalLocationMillDashboardFilteredResponse,
    IGetUptimesVsDowntimesTotalsByProductionLineMillDashboardResponse,
    getOeeDowntimeByMcFailureTypeMillDashboard,
    getOeeSummaryByMcDayMillDashboard,
    getOeeSummaryDashboardFilterOptions,
    IGetOeeDowntimeByMcFailureTypeMillDashboardResponse,
    IGetOeeSummaryByMcDayMillDashboardResponse,
    IGetOeeSummaryDashboardFilterOptionsResponse,
} from "api/queries/millDashboardAPI";

/**
 * Args shared by the OEE Summary SQL functions. Production lines are filtered client-side so the
 * per-line card always lists every line; p_as_of is left to the server clock.
 */
const buildOeeSummaryArgs = (filters: IOeeSummaryQueryFilters, millId: number) => ({
    p_start_date: getStartOfDateRange(filters.startDate),
    p_end_date: getEndOfDateRange(filters.endDate),
    p_mill_id: millId,
    p_production_line_ids: null,
    p_machine_center_ids:
        filters.machineCenter.length > 0 && !filters.machineCenter.includes(allElements)
            ? `{${filters.machineCenter}}`
            : null,
    p_shift_id: filters.shift !== allElements ? (filters.shift as number) : null,
});

export const MillsEntity: IMillSettingsEntity = {
    GetMillSettingsData: () => {
        const { loading, error, data, refetch } = useQuery(getMillSettingsData, {
            fetchPolicy: "no-cache",
        });
        let dayArray: IDay[] = [];
        let measureMetricArray: IMeasureMetric[] = [];
        let keySuccessFactorsArray: IKeySuccessFactors[] = [];

        if (data) {
            dayArray = data.day;
            measureMetricArray = data.measure_metric;
            keySuccessFactorsArray = data.key_success_factors;
        }
        return { loading, dayArray, measureMetricArray, keySuccessFactorsArray, error, refetch };
    },
    GetMillCountryCode: (mill_id: number) => {
        const { loading, error, data, refetch } = useQuery(getMillCountryCode, { variables: { mill_id } });
        const [millCountryCode, setMillCountryCode] = useState<string>("");
        const [apolloQueryError, setApolloQueryError] = useState<ApolloError | undefined>(undefined);

        useEffect(() => {
            if (data) {
                const millCountryCode: string = data.mill[0].country?.code;
                setMillCountryCode(millCountryCode);
            }
            if (error) {
                setApolloQueryError(error);
            }
        }, [data, error]);

        return { loading, millCountryCode, apolloQueryError, refetch };
    },
    GetMillTimezone: (mill_id: number) => {
        const { loading, error, data, refetch } = useQuery(getMillTimezone, { variables: { mill_id } });
        const [millTimezone, setMillTimezone] = useState<string>("");
        const [apolloQueryError, setApolloQueryError] = useState<ApolloError | undefined>(undefined);

        useEffect(() => {
            if (data) {
                const millTimezone: string = data.mill_data[0]?.timezone;
                setMillTimezone(millTimezone);
            }
            if (error) {
                setApolloQueryError(error);
            }
        }, [data, error]);

        return { loading, millTimezone, apolloQueryError, refetch };
    },
    UpdateMillSettings: () => {
        const [upsertMillShiftsKSFCrewsDataSettings, { error, loading }] = useMutation(upsertMillShiftsKSFCrewsData);
        return { error, upsertMillShiftsKSFCrewsDataSettings, loading };
    },
    CreateMillWithManagerOsornoBackoffice: () => {
        const [addMillWithManager, { error }] = useMutation(insertMillWithAdminUser);

        return { apolloMutationError: error, addMillWithManager };
    },
    UpsertMillWithManagerOsornoBackoffice: () => {
        const [upsertMillWithManager, { error }] = useMutation(upsertMillWithAdminUser);

        return { apolloMutationError: error, upsertMillWithManager };
    },
    GetEventReportsFailureTypesStatsByMill: (mill_id: number, filters: IMillFilters) => {
        const { loading, error, data, refetch } = useQuery(getEventFailureCodeSummaryPerMCQuery, {
            variables: {
                where: {
                    _or: [
                        {
                            failure_code: {
                                _or: [
                                    {
                                        code: { _neq: END_OF_SHIFT_FAILURE_CODE_NAME },
                                    },
                                    {
                                        code: { _is_null: true },
                                    },
                                ],
                            },
                        },
                        {
                            failure_code_id: { _is_null: true },
                        },
                    ],
                    failure_type_id: { _is_null: false },
                    created_at: {
                        _gte: getStartOfDateRange(filters.startDate as Date),
                        _lte: getEndOfDateRange(filters.endDate as Date),
                    },
                    machine_center: {
                        ...(filters.functionalLocation !== allElements && {
                            areas: {
                                functional_locations: {
                                    functional_location_id: {
                                        _eq: filters.functionalLocation,
                                    },
                                },
                            },
                        }),
                        ...(filters.machineCenter?.length! > 0 &&
                            !filters.machineCenter?.includes(allElements) && {
                                machine_center_id: {
                                    _in: filters.machineCenter,
                                },
                            }),
                        production_line: {
                            ...(filters.productionLine !== allElements && {
                                production_line_id: {
                                    _eq: filters.productionLine,
                                },
                            }),
                            mill_id: {
                                _eq: mill_id,
                            },
                        },
                    },
                    ...(filters.failureTypes?.length! > 0 &&
                        !filters.failureTypes?.includes(allElements) && {
                            failure_type_id: {
                                _in: filters.failureTypes,
                            },
                        }),
                    ...(filters.shift !== allElements && {
                        shift_id: {
                            _eq: filters.shift,
                        },
                    }),
                },
            },
        });
        const [eventReportsFailureDetailsStatsByMill, setEventReportsFailureDetailsStatsByMill] = useState<
            IEventFailureCodeSummaryPerMC[]
        >([]);
        const [apolloQueryError, setApolloQueryError] = useState<ApolloError | undefined>(undefined);

        useEffect(() => {
            if (data) {
                const eventReportsFailureDetailsStatsByMillArray: IEventFailureCodeSummaryPerMC[] =
                    data.event_failure_code_summary_per_mc;
                setEventReportsFailureDetailsStatsByMill(eventReportsFailureDetailsStatsByMillArray);
            }
            if (error) {
                setApolloQueryError(error);
            }
        }, [data, error]);

        return { loading, eventReportsFailureDetailsStatsByMill, apolloQueryError, refetch };
    },
    GetFailureTypes13WeekTrend: (mill_id: number, filters: IMillFilters) => {
        const { loading, error, data, refetch } = useQuery(getEventFailureCodeSummaryPerMCQuery, {
            variables: {
                where: {
                    created_at: {
                        _gte: moment(filters.endDate ? new Date(filters.endDate) : new Date())
                            .subtract(14, "weeks")
                            .startOf("day"),
                        _lte: getEndOfDateRange(filters.endDate as Date),
                    },
                    failure_type_id: { _is_null: false },
                    machine_center: {
                        ...(filters.machineCenter?.length! > 0 &&
                            !filters.machineCenter?.includes(allElements) && {
                                machine_center_id: {
                                    _in: filters.machineCenter,
                                },
                            }),
                        production_line: {
                            ...(filters.productionLine !== allElements && {
                                production_line_id: {
                                    _eq: filters.productionLine,
                                },
                            }),
                            mill_id: {
                                _eq: mill_id,
                            },
                        },
                    },
                    ...(filters.functionalLocation &&
                        filters.functionalLocation !== allElements && {
                            functional_location_id: {
                                _eq: filters.functionalLocation,
                            },
                        }),
                    ...(filters.shift !== allElements && {
                        shift_id: {
                            _eq: filters.shift,
                        },
                    }),
                    ...(filters.failureTypes?.length! > 0 &&
                        !filters.failureTypes?.includes(allElements) && {
                            failure_type_id: {
                                _in: filters.failureTypes,
                            },
                        }),
                },
            },
        });
        const [failureTypes13WeekTrend, setFailureTypes13WeekTrend] = useState<IEventFailureCodeSummaryPerMC[]>([]);
        const [apolloQueryError, setApolloQueryError] = useState<ApolloError | undefined>(undefined);

        useEffect(() => {
            if (data) {
                const failureTypes13WeekTrendArray: IEventFailureCodeSummaryPerMC[] =
                    data.event_failure_code_summary_per_mc;
                setFailureTypes13WeekTrend(failureTypes13WeekTrendArray);
            }
            if (error) {
                setApolloQueryError(error);
            }
        }, [data, error]);

        return { loading, failureTypes13WeekTrend, apolloQueryError, refetch };
    },
    GetMachineCentersDowntimesByMill(mill_id, filters) {
        const { loading, error, data, refetch } = useQuery(getEventFailureCodeSummaryPerMCQuery, {
            variables: {
                where: {
                    _or: [
                        {
                            failure_code: {
                                _or: [
                                    {
                                        code: { _neq: END_OF_SHIFT_FAILURE_CODE_NAME },
                                    },
                                    {
                                        code: { _is_null: true },
                                    },
                                ],
                            },
                        },
                        {
                            failure_code_id: { _is_null: true },
                        },
                    ],
                    failure_type_id: { _is_null: false },
                    created_at: {
                        _gte: getStartOfDateRange(filters.startDate as Date),
                        _lte: getEndOfDateRange(filters.endDate as Date),
                    },
                    machine_center: {
                        ...(filters.functionalLocation !== allElements && {
                            areas: {
                                functional_locations: {
                                    functional_location_id: {
                                        _eq: filters.functionalLocation,
                                    },
                                },
                            },
                        }),
                        ...(filters.machineCenter?.length! > 0 &&
                            !filters.machineCenter?.includes(allElements) && {
                                machine_center_id: {
                                    _in: filters.machineCenter,
                                },
                            }),
                        production_line: {
                            ...(filters.productionLine !== allElements && {
                                production_line_id: {
                                    _eq: filters.productionLine,
                                },
                            }),
                            mill_id: {
                                _eq: mill_id,
                            },
                        },
                    },
                    ...(filters.failureTypes?.length! > 0 &&
                        !filters.failureTypes?.includes(allElements) && {
                            failure_type_id: {
                                _in: filters.failureTypes,
                            },
                        }),
                    ...(filters.shift !== allElements && {
                        shift_id: {
                            _eq: filters.shift,
                        },
                    }),
                },
            },
        });
        const [machineCentersDowntimesByMill, setmachineCentersDowntimesByMill] = useState<
            IEventFailureCodeSummaryPerMC[]
        >([]);
        const [apolloQueryError, setApolloQueryError] = useState<ApolloError | undefined>(undefined);

        useEffect(() => {
            if (data) {
                const machineCentersDowntimesByMillArray: IEventFailureCodeSummaryPerMC[] =
                    data.event_failure_code_summary_per_mc;
                setmachineCentersDowntimesByMill(machineCentersDowntimesByMillArray);
            }
            if (error) {
                setApolloQueryError(error);
            }
        }, [data, error]);
        return { loading, machineCentersDowntimesByMill, apolloQueryError, refetch };
    },
    GetTopFiveFunctionalLocationsByMill(mill_id, filters) {
        const { loading, error, data, refetch } = useQuery(getEventFunctionalLocationSummaryPerMCQuery, {
            variables: {
                where: {
                    created_at: {
                        _gte: getStartOfDateRange(filters.startDate as Date),
                        _lte: getEndOfDateRange(filters.endDate as Date),
                    },
                    machine_center: {
                        ...(filters.machineCenter?.length! > 0 &&
                            !filters.machineCenter?.includes(allElements) && {
                                machine_center_id: {
                                    _in: filters.machineCenter,
                                },
                            }),
                        production_line: {
                            ...(filters.productionLine !== allElements && {
                                production_line_id: {
                                    _eq: filters.productionLine,
                                },
                            }),
                            mill_id: {
                                _eq: mill_id,
                            },
                        },
                    },
                    functional_location: {
                        ...(filters.functionalLocation !== allElements && {
                            functional_location_id: {
                                _eq: filters.functionalLocation,
                            },
                        }),
                    },
                    ...(filters.failureTypes?.length! > 0 &&
                        !filters.failureTypes?.includes(allElements) && {
                            failure_type_id: {
                                _in: filters.failureTypes,
                            },
                        }),
                    ...(filters.shift !== allElements && {
                        shift_id: {
                            _eq: filters.shift,
                        },
                    }),
                },
            },
        });
        const [topFiveFunctionalLocationsByMill, setTopFiveFunctionalLocationsByMill] = useState<
            IEventFunctionalLocationSummaryPerMC[]
        >([]);
        const [apolloQueryError, setApolloQueryError] = useState<ApolloError | undefined>(undefined);

        useEffect(() => {
            if (data) {
                const topFiveFunctionalLocationsByMillArray: IEventFunctionalLocationSummaryPerMC[] =
                    data.event_functional_location_summary_per_mc;
                setTopFiveFunctionalLocationsByMill(topFiveFunctionalLocationsByMillArray);
            }
            if (error) {
                setApolloQueryError(error);
            }
        }, [data, error]);
        return { loading, topFiveFunctionalLocationsByMill, apolloQueryError, refetch };
    },
    GetMillShifts: (mill_id: number) => {
        const { loading, error, data } = useQuery(
            getMillShifts,
            useMemo(
                () => ({
                    variables: { mill_id },
                    fetchPolicy: "no-cache",
                }),
                [mill_id],
            ),
        );
        const [shifts, setShifts] = useState<IShift[]>([]);
        const [apolloQueryError, setApolloQueryError] = useState<ApolloError | undefined>(undefined);

        useEffect(() => {
            if (data) {
                const shiftsArray: IShift[] = data.shift;
                setShifts(shiftsArray);
            }
            if (error) {
                setApolloQueryError(error);
            }
        }, [data, error]);
        return { loading, shifts, apolloQueryError };
    },
    GetAllMillData: (mill_id: number) => {
        const { loading, error, data, refetch } = useQuery(
            getAllMillData,
            useMemo(
                () => ({
                    variables: { mill_id, currentDate: new Date().toISOString() },
                }),
                [mill_id],
            ),
        );
        let millData: IAllMillData | undefined = undefined;
        if (data) {
            millData = {
                mill: data.mill[0],
                users: data.user,
                days: data.day,
            };
        }
        return { loading, millData, apolloQueryError: error, refetch };
    },
    GetFailureCodesByFailureType: (mill_id: number, filters: IMillFilters) => {
        const {
            loading,
            error: apolloQueryError,
            data,
        } = useQuery(getEventFailureCodeSummaryPerMCQuery, {
            variables: {
                where: {
                    failure_type_id: { _is_null: false },
                    failure_code: {
                        _or: [
                            {
                                code: { _neq: END_OF_SHIFT_FAILURE_CODE_NAME },
                            },
                            {
                                code: { _is_null: true },
                            },
                        ],
                    },
                    created_at: {
                        _gte: getStartOfDateRange(filters.startDate as Date),
                        _lte: getEndOfDateRange(filters.endDate as Date),
                    },

                    ...(filters.failureTypes?.length! > 0 &&
                        !filters.failureTypes?.includes(allElements) && {
                            failure_type_id: {
                                _in: filters.failureTypes,
                            },
                        }),
                    machine_center: {
                        ...(filters.functionalLocation !== allElements && {
                            areas: {
                                functional_locations: {
                                    functional_location_id: {
                                        _eq: filters.functionalLocation,
                                    },
                                },
                            },
                        }),
                        ...(filters.machineCenter?.length! > 0 &&
                            !filters.machineCenter?.includes(allElements) && {
                                machine_center_id: {
                                    _in: filters.machineCenter,
                                },
                            }),
                        production_line: {
                            ...(filters.productionLine !== allElements && {
                                production_line_id: {
                                    _eq: filters.productionLine,
                                },
                            }),
                            mill_id: {
                                _eq: mill_id,
                            },
                        },
                    },
                    ...(filters.shift !== allElements && {
                        shift_id: {
                            _eq: filters.shift,
                        },
                    }),
                },
            },
        });

        let failureCodesByFailureType = [] as IEventFailureCodeSummaryPerMC[];
        if (data) {
            failureCodesByFailureType = data.event_failure_code_summary_per_mc;
        }
        return { loading, failureCodesByFailureType, apolloQueryError };
    },
    isMillOnboarding: async () => {
        // The API Administrator (OS-2557) has no mill/shift read access, and onboarding is
        // meaningless for it — skip the query outright rather than relying on the catch below.
        if (isApiAdministrator()) {
            return false;
        }

        try {
            const apolloClient = new API().client;
            const response: {
                data: {
                    mill: {
                        mill_id: number;
                        mill_data: {
                            mill_data_id: number;
                        } | null;
                        shifts: {
                            shift_id: number;
                        }[];
                    }[];
                };
            } = await apolloClient.query({
                query: isOnboardingCorporateBackoffice,
            });

            const millExist = response?.data?.mill?.length > 0;
            const oneMill = response?.data?.mill?.length === 1;
            const millWithoutShiftsOrMillData =
                !response?.data?.mill?.[0]?.mill_data || response?.data?.mill?.[0]?.shifts?.length === 0;

            return millExist && oneMill && millWithoutShiftsOrMillData;
        } catch (error) {
            // Login.view.tsx awaits this before every post-login navigate() and neither it nor
            // handleSubmit catches — so a rejection here strands the user on the login screen
            // holding a valid token. Not onboarding is the safe answer for any failure.
            //
            // Logged rather than swallowed silently: this also covers a transient failure for a
            // Corporation that IS mid-onboarding, which now routes into the normal CBO as though
            // setup were complete. Without a trace there is nothing to explain that afterwards.
            console.error("isMillOnboarding lookup failed; treating the mill as not onboarding", error);
            return false;
        }
    },
    EnableOrDisableMill: () => {
        const [updateEnableOrDisableMill, { error, loading }] = useMutation(enableOrDisableMill);
        return { error, updateEnableOrDisableMill, loading };
    },
    GetMillUptimesVsDowntimes: (mill_id: number, filters: IMillFilters) => {
        const { loading, error, data, refetch } = useQuery(getEventFailureCodeSummaryPerMCQuery, {
            variables: {
                where: {
                    _or: [
                        { failure_code_id: { _is_null: true } },
                        {
                            failure_code: {
                                _or: [{ code: { _neq: END_OF_SHIFT_FAILURE_CODE_NAME } }, { code: { _is_null: true } }],
                            },
                        },
                    ],
                    created_at: {
                        _gte: getStartOfDateRange(filters.startDate as Date),
                        _lte: getEndOfDateRange(filters.endDate as Date),
                    },
                    ...(filters.shift !== allElements && {
                        shift_id: {
                            _eq: filters.shift,
                        },
                    }),
                    machine_center: {
                        production_line: {
                            ...(filters.productionLine !== allElements && {
                                production_line_id: {
                                    _eq: filters.productionLine,
                                },
                            }),
                            mill_id: {
                                _eq: mill_id,
                            },
                        },
                    },
                },
            },
        });

        const [millUptimesVsDowntimes, setMillUptimesVsDowntimes] = useState<IEventFailureCodeSummaryPerMC[]>([]);
        const [apolloQueryError, setApolloQueryError] = useState<ApolloError | undefined>(undefined);

        useEffect(() => {
            if (data) {
                const millUptimesVsDowntimes: IEventFailureCodeSummaryPerMC[] = data.event_failure_code_summary_per_mc;
                setMillUptimesVsDowntimes(millUptimesVsDowntimes);
            }
            if (error) {
                setApolloQueryError(error);
            }
        }, [data, error]);

        return { loading, millUptimesVsDowntimes, apolloQueryError, refetch };
    },
    GetMillMaximumOvertime: (mill_id: number) => {
        const { loading, error, data } = useQuery(getMillMaximumOvertime, {
            variables: { mill_id },
        });
        let maximumOvertime = 0;
        if (data) {
            maximumOvertime = data.mill[0].maximum_overtime;
        }
        return { loading, maximumOvertime, apolloQueryError: error };
    },
    GetDowntimeSummaryTotalsByFailureTypeMillDashboard: (filters: IMillFilters, millId: number) => {
        const { loading, error, data, refetch } = useQuery<{
            downtimeSummary: IGetDowntimeSummaryByFailureTypeMillDashboardResponse[];
        }>(getDowntimeSummaryByFailureTypeMillDashboard, {
            variables: {
                args: {
                    p_start_date: getStartOfDateRange(filters.startDate as Date),
                    p_end_date: getEndOfDateRange(filters.endDate as Date),
                    p_mill_id: millId,
                    p_production_line_ids:
                        filters.productionLine && filters.productionLine !== allElements
                            ? `{${filters.productionLine}}`
                            : null,
                    p_machine_center_ids:
                        filters.machineCenter?.length! > 0 && !filters.machineCenter?.includes(allElements)
                            ? `{${filters.machineCenter}}`
                            : null,
                    p_failure_type_ids:
                        filters.failureTypes?.length! > 0 && !filters.failureTypes?.includes(allElements)
                            ? `{${filters.failureTypes}}`
                            : null,
                    p_shift_id: filters.shift && filters.shift !== allElements ? (filters.shift as number) : null,
                    p_functional_location_id:
                        filters.functionalLocation && filters.functionalLocation !== allElements
                            ? (filters.functionalLocation as number)
                            : null,
                },
            },
        });
        const [downtimeSummaryByFailureTypeTotal, setDowntimeSummaryByFailureTypeTotal] = useState<
            IDowntimeSummaryByFailureTypeTotal[]
        >([]);
        const [apolloQueryError, setApolloQueryError] = useState<ApolloError | undefined>(undefined);

        useEffect(() => {
            if (data?.downtimeSummary) {
                const downtimeSummaryByFailureTypeTotalArray: IDowntimeSummaryByFailureTypeTotal[] =
                    data.downtimeSummary.map((item) => ({
                        failure_type_id: item.failure_type_id,
                        name: item.failure_type.name,
                        color_code: item.failure_type.color_code,
                        is_location_specific: item.failure_type.is_location_specific,
                        default_failure_type_translations: item.failure_type.default_failure_type_translations,
                        event_failure_code_summary_per_mcs_aggregate: {
                            aggregate: {
                                sum: {
                                    seconds: item.total_seconds,
                                    occurrences: item.total_occurrences,
                                },
                            },
                        },
                    }));
                setDowntimeSummaryByFailureTypeTotal(downtimeSummaryByFailureTypeTotalArray);
            }
            if (error) {
                setApolloQueryError(error);
            }
        }, [data, error]);

        return { loading, downtimeSummaryByFailureTypeTotal, apolloQueryError, refetch };
    },
    GetDowntimeSummaryTotalsByFailureCodeMillDashboard: (filters: IMillFilters, millId: number, id: number) => {
        const { loading, error, data, refetch } = useQuery<{
            downtimeSummary: IGetDowntimeSummaryByFailureCodeMillDashboardResponse[];
        }>(getDowntimeSummaryByFailureCodeMillDashboardFiltered, {
            variables: {
                where: {
                    failure_code_id: {
                        _is_null: false,
                    },
                },
                args: {
                    p_start_date: getStartOfDateRange(filters.startDate as Date),
                    p_end_date: getEndOfDateRange(filters.endDate as Date),
                    p_mill_id: millId,
                    p_failure_type_id: id,
                    p_production_line_ids:
                        filters.productionLine && filters.productionLine !== allElements
                            ? `{${filters.productionLine}}`
                            : null,
                    p_machine_center_ids:
                        filters.machineCenter?.length! > 0 && !filters.machineCenter?.includes(allElements)
                            ? `{${filters.machineCenter}}`
                            : null,
                    p_shift_id: filters.shift && filters.shift !== allElements ? (filters.shift as number) : null,
                    p_functional_location_id:
                        filters.functionalLocation && filters.functionalLocation !== allElements
                            ? (filters.functionalLocation as number)
                            : null,
                },
            },
        });
        const [downtimeSummaryByFailureCodeTotal, setDowntimeSummaryByFailureCodeTotal] = useState<
            IDowntimeSummaryByFailureCodeTotal[]
        >([]);
        const [apolloQueryError, setApolloQueryError] = useState<ApolloError | undefined>(undefined);

        useEffect(() => {
            if (data?.downtimeSummary) {
                const downtimeSummaryByFailureCodeTotalArray: IDowntimeSummaryByFailureCodeTotal[] =
                    data.downtimeSummary
                        .filter((summary) => summary.total_seconds > 0)
                        .map((item) => ({
                            failure_code_id: item.failure_code_id,
                            name: item.failure_code.name,
                            default_failure_code_translations:
                                item.failure_code.default_failure_code_translations || [],
                            summary: {
                                aggregate: {
                                    sum: {
                                        seconds: item.total_seconds,
                                        occurrences: item.total_occurrences,
                                    },
                                },
                            },
                        }));
                setDowntimeSummaryByFailureCodeTotal(downtimeSummaryByFailureCodeTotalArray);
            }
            if (error) {
                setApolloQueryError(error);
            }
        }, [data, error]);

        return { loading, downtimeSummaryByFailureCodeTotal, apolloQueryError, refetch };
    },
    GetDowntimeSummaryTotalsBySecondaryFailureCodeMillDashboard: (
        filters: IMillFilters,
        millId: number,
        id: number,
    ) => {
        const { loading, error, data, refetch } = useQuery<{
            downtimeSummary: IGetDowntimeSummaryBySecondaryFailureCodeMillDashboardResponse[];
        }>(getDowntimeSummaryBySecondaryFailureCodeMillDashboardFiltered, {
            variables: {
                args: {
                    p_start_date: getStartOfDateRange(filters.startDate as Date),
                    p_end_date: getEndOfDateRange(filters.endDate as Date),
                    p_mill_id: millId,
                    p_failure_code_id: id,
                    p_production_line_ids:
                        filters.productionLine && filters.productionLine !== allElements
                            ? `{${filters.productionLine}}`
                            : null,
                    p_machine_center_ids:
                        filters.machineCenter?.length! > 0 && !filters.machineCenter?.includes(allElements)
                            ? `{${filters.machineCenter}}`
                            : null,
                    p_shift_id: filters.shift && filters.shift !== allElements ? (filters.shift as number) : null,
                    p_functional_location_id:
                        filters.functionalLocation && filters.functionalLocation !== allElements
                            ? (filters.functionalLocation as number)
                            : null,
                },
            },
        });
        const [downtimeSummaryBySecondaryFailureCodeTotal, setDowntimeSummaryBySecondaryFailureCodeTotal] = useState<
            IDowntimeSummaryBySecondaryFailureCodeTotal[]
        >([]);
        const [apolloQueryError, setApolloQueryError] = useState<ApolloError | undefined>(undefined);

        useEffect(() => {
            if (data?.downtimeSummary) {
                const downtimeSummaryBySecondaryFailureCodeTotalArray = data.downtimeSummary.filter(
                    (summary) => summary.total_seconds > 0,
                );
                setDowntimeSummaryBySecondaryFailureCodeTotal(downtimeSummaryBySecondaryFailureCodeTotalArray);
            }
            if (error) {
                setApolloQueryError(error);
            }
        }, [data, error]);

        return { loading, downtimeSummaryBySecondaryFailureCodeTotal, apolloQueryError, refetch };
    },
    GetTotalDowntimeSummaryByTypePerMachineCenterMillDashboard: (
        filters: IMillFilters,
        summaryOption: SummaryOption,
        millId: number,
        id?: number,
    ) => {
        let failureTypeIds: number[] | null = null;
        if (summaryOption === SummaryOption.BY_TYPE && id) {
            failureTypeIds = [id];
        } else if (
            summaryOption === SummaryOption.ALL &&
            filters.failureTypes?.length! > 0 &&
            !filters.failureTypes?.includes(allElements)
        ) {
            failureTypeIds = filters.failureTypes as number[];
        }

        const { loading, error, data, refetch } = useQuery(
            getDowntimeSummaryFailureTypePerMachineCenterMillDashboardFiltered,
            {
                variables: {
                    args: {
                        p_start_date: getStartOfDateRange(filters.startDate as Date),
                        p_end_date: getEndOfDateRange(filters.endDate as Date),
                        p_mill_id: millId,
                        p_production_line_ids:
                            filters.productionLine && filters.productionLine !== allElements
                                ? `{${filters.productionLine}}`
                                : null,
                        p_machine_center_ids:
                            filters.machineCenter?.length! > 0 && !filters.machineCenter?.includes(allElements)
                                ? `{${filters.machineCenter}}`
                                : null,
                        p_failure_type_ids: failureTypeIds ? `{${failureTypeIds}}` : null,
                        p_shift_id: filters.shift && filters.shift !== allElements ? (filters.shift as number) : null,
                        p_functional_location_id:
                            filters.functionalLocation && filters.functionalLocation !== allElements
                                ? (filters.functionalLocation as number)
                                : null,
                    },
                },
            },
        );
        const [downtimeSummaryPerMachineCenter, setDowntimeSummaryPerMachineCenter] = useState<
            IDowntimeSummaryTotalsByTypePerMachineCenter[]
        >([]);
        const [apolloQueryError, setApolloQueryError] = useState<ApolloError | undefined>(undefined);

        useEffect(() => {
            if (data?.baseDowntimes) {
                const downtimeSummaryByFailureTypePerMachineCenterArray: IDowntimeSummaryTotalsByTypePerMachineCenter[] =
                    data.baseDowntimes;
                //Here we have to filter the failure types that have seconds null because Hasura doesn't support filtering on aggregate fields directly.
                const downtimeSummaryByFailureTypePerMachineCenterArrayFiltered: IDowntimeSummaryTotalsByTypePerMachineCenter[] =
                    downtimeSummaryByFailureTypePerMachineCenterArray.filter((summary) => summary.total_seconds > 0);
                setDowntimeSummaryPerMachineCenter(downtimeSummaryByFailureTypePerMachineCenterArrayFiltered);
            }
            if (error) {
                setApolloQueryError(error);
            }
        }, [data, error]);

        return {
            loading,
            downtimeSummaryPerMachineCenter,
            apolloQueryError,
            refetch,
        };
    },
    GetOeeSummaryByMachineCenterDayMillDashboard: (filters: IOeeSummaryQueryFilters, millId: number) => {
        const { loading, error, data, refetch } = useQuery<{
            oeeSummaryRows: IGetOeeSummaryByMcDayMillDashboardResponse[];
        }>(getOeeSummaryByMcDayMillDashboard, {
            fetchPolicy: "cache-and-network",
            skip: !millId,
            variables: { args: buildOeeSummaryArgs(filters, millId) },
        });

        const oeeSummaryRows = useMemo(
            () =>
                (data?.oeeSummaryRows ?? []).map((row) => ({
                    downtimeSeconds: row.downtime_seconds,
                    earnedSeconds: row.earned_seconds,
                    goodCount: row.good_count,
                    machineCenterId: row.machine_center_id,
                    machineCenterName: row.machine_center.short_name || row.machine_center.name,
                    measuredRunTimeSeconds: row.measured_run_time_seconds,
                    pptSeconds: row.ppt_seconds,
                    productionDate: row.production_date,
                    productionLineId: row.production_line_id,
                    productionLineName: row.production_line.code || row.production_line.name,
                    runTimeSeconds: row.run_time_seconds,
                    totalCount: row.total_count,
                })),
            [data],
        );

        return { loading, oeeSummaryRows, apolloQueryError: error, refetch };
    },
    GetOeeDowntimeByMachineCenterFailureTypeMillDashboard: (filters: IOeeSummaryQueryFilters, millId: number) => {
        const { loading, error, data, refetch } = useQuery<{
            oeeDowntimeRows: IGetOeeDowntimeByMcFailureTypeMillDashboardResponse[];
        }>(getOeeDowntimeByMcFailureTypeMillDashboard, {
            fetchPolicy: "cache-and-network",
            skip: !millId,
            variables: { args: buildOeeSummaryArgs(filters, millId) },
        });

        const oeeDowntimeRows = useMemo(
            () =>
                (data?.oeeDowntimeRows ?? []).map((row) => ({
                    downtimeSeconds: row.downtime_seconds,
                    failureType: row.failure_type,
                    failureTypeId: row.failure_type_id,
                    machineCenterId: row.machine_center_id,
                    machineCenterName: row.machine_center.short_name || row.machine_center.name,
                    productionLineId: row.production_line_id,
                })),
            [data],
        );

        return { loading, oeeDowntimeRows, apolloQueryError: error, refetch };
    },
    GetOeeSummaryDashboardFilterOptions: (millId: number, range: IProductionDateRange) => {
        const { loading, error, data } = useQuery<IGetOeeSummaryDashboardFilterOptionsResponse>(
            getOeeSummaryDashboardFilterOptions,
            {
                skip: !millId,
                variables: {
                    end_date: moment(range.endDate, "YYYY-MM-DD").add(1, "day").format("YYYY-MM-DD"),
                    mill_id: millId,
                    start_date: range.startDate,
                },
            },
        );

        const machineCenters = useMemo(
            () =>
                (data?.oee_performance_calculation_summary ?? [])
                    .map(({ machine_center: machineCenter }) => ({
                        machineCenterId: machineCenter.machine_center_id,
                        name: machineCenter.short_name || machineCenter.name,
                        productionLineId: machineCenter.production_line_id,
                    }))
                    .sort((a, b) => a.name.localeCompare(b.name)),
            [data],
        );
        const shifts = useMemo(
            () => (data?.shift ?? []).map((shift) => ({ name: shift.name, shiftId: shift.shift_id })),
            [data],
        );

        return { loading, machineCenters, shifts, apolloQueryError: error };
    },
    GetTotalDowntimeSummaryByCodePerMachineCenterMillDashboard: (
        filters: IMillFilters,
        summaryOption: SummaryOption,
        millId: number,
        codeId?: number,
        secondaryCodeId?: number,
    ) => {
        const { loading, error, data, refetch } = useQuery<{
            baseDowntimes: IGetDowntimeSummaryFailureCodeByMachineCenterMillDashboardFilteredResponse[];
        }>(
            summaryOption === SummaryOption.BY_CODE
                ? getDowntimeSummaryFailureCodeByMachineCenterMillDashboardFiltered
                : getDowntimeSummarySecondaryFailureCodeByMachineCenterMillDashboardFiltered,
            {
                skip: summaryOption === SummaryOption.BY_SECONDARY_CODE ? !secondaryCodeId || !codeId : !codeId,
                variables: {
                    args: {
                        p_start_date: getStartOfDateRange(filters.startDate as Date),
                        p_end_date: getEndOfDateRange(filters.endDate as Date),
                        p_mill_id: millId,
                        p_failure_code_id: codeId,
                        ...(summaryOption === SummaryOption.BY_SECONDARY_CODE && {
                            p_secondary_failure_code_id: secondaryCodeId,
                        }),
                        p_production_line_ids:
                            filters.productionLine && filters.productionLine !== allElements
                                ? `{${filters.productionLine}}`
                                : null,
                        p_machine_center_ids:
                            filters.machineCenter?.length! > 0 && !filters.machineCenter?.includes(allElements)
                                ? `{${filters.machineCenter}}`
                                : null,
                        p_shift_id: filters.shift && filters.shift !== allElements ? (filters.shift as number) : null,
                        p_functional_location_id:
                            filters.functionalLocation && filters.functionalLocation !== allElements
                                ? (filters.functionalLocation as number)
                                : null,
                    },
                },
            },
        );
        const [downtimeSummaryPerMachineCenter, setDowntimeSummaryPerMachineCenter] = useState<
            IDowntimeSummaryTotalsByCodePerMachineCenter[]
        >([]);
        const [apolloQueryError, setApolloQueryError] = useState<ApolloError | undefined>(undefined);

        useEffect(() => {
            if (data?.baseDowntimes) {
                const downtimeSummaryByFailureTypePerMachineCenterArray = data.baseDowntimes.filter(
                    (summary) => summary.total_seconds > 0,
                );
                setDowntimeSummaryPerMachineCenter(downtimeSummaryByFailureTypePerMachineCenterArray);
            }
            if (error) {
                setApolloQueryError(error);
            }
        }, [data, error]);

        return {
            loading,
            downtimeSummaryPerMachineCenter,
            apolloQueryError,
            refetch,
        };
    },
    GetUptimesVsDowntimesTotalsPerMachineCenterMillDashboard: (filters: IMillFilters, millId: number) => {
        const { loading, error, data, refetch } = useQuery<{
            productionLineSummary: IGetUptimesVsDowntimesTotalsByProductionLineMillDashboardResponse[];
        }>(getUptimesVsDowntimesTotalsByProductionLineMillDashboard, {
            variables: {
                args: {
                    p_start_date: getStartOfDateRange(filters.startDate as Date),
                    p_end_date: getEndOfDateRange(filters.endDate as Date),
                    p_mill_id: millId,
                    p_machine_center_ids:
                        filters.machineCenter?.length! > 0 && !filters.machineCenter?.includes(allElements)
                            ? `{${filters.machineCenter}}`
                            : null,
                    p_failure_type_ids:
                        filters.failureTypes?.length! > 0 && !filters.failureTypes?.includes(allElements)
                            ? `{${filters.failureTypes}}`
                            : null,
                    p_shift_id: filters.shift && filters.shift !== allElements ? (filters.shift as number) : null,
                    p_functional_location_id:
                        filters.functionalLocation && filters.functionalLocation !== allElements
                            ? (filters.functionalLocation as number)
                            : null,
                },
            },
        });
        const [productionLineSummary, setProductionLineSummary] = useState<
            IDowntimeUptimeSummaryTotalsByProductionLine[]
        >([]);
        const [apolloQueryError, setApolloQueryError] = useState<ApolloError | undefined>(undefined);

        useEffect(() => {
            if (data?.productionLineSummary) {
                const productionLineSummaryArray: IDowntimeUptimeSummaryTotalsByProductionLine[] =
                    data.productionLineSummary
                        .filter((item) => item.downtime_seconds > 0)
                        .map((item) => ({
                            production_line_id: item.production_line_id,
                            downtime_seconds: item.downtime_seconds,
                            downtime_occurrences: item.downtime_occurrences,
                            uptime_seconds: item.uptime_seconds,
                            uptime_occurrences: item.uptime_occurrences,
                            production_line: item.production_line,
                        }));
                setProductionLineSummary(productionLineSummaryArray);
            }
            if (error) {
                setApolloQueryError(error);
            }
        }, [data, error]);

        return {
            loading,
            productionLineSummary,
            apolloQueryError,
            refetch,
        };
    },
    GetTotalDowntimeSummaryByCodePerFunctionalLocationMillDashboard: (
        filters: IMillFilters,
        machineCenterId: number,
        codeId?: number,
    ) => {
        const { loading, error, data, refetch } = useQuery<{
            downtimeSummary: IGetDowntimeSummaryByFunctionalLocationMillDashboardFilteredResponse[];
        }>(getDowntimeSummaryByFunctionalLocationMillDashboardFiltered, {
            skip: !machineCenterId || !codeId,
            variables: {
                args: {
                    p_start_date: getStartOfDateRange(filters.startDate as Date),
                    p_end_date: getEndOfDateRange(filters.endDate as Date),
                    p_machine_center_id: machineCenterId,
                    p_failure_code_id: codeId as number,
                    p_production_line_ids:
                        filters.productionLine && filters.productionLine !== allElements
                            ? `{${filters.productionLine}}`
                            : null,
                    p_shift_id: filters.shift && filters.shift !== allElements ? (filters.shift as number) : null,
                    p_functional_location_id:
                        filters.functionalLocation && filters.functionalLocation !== allElements
                            ? (filters.functionalLocation as number)
                            : null,
                },
            },
        });
        const [downtimeSummaryPerFunctionalLocation, setDowntimeSummaryPerFunctionalLocation] = useState<
            IDowntimeSummaryByFunctionalLocationTotal[]
        >([]);
        const [apolloQueryError, setApolloQueryError] = useState<ApolloError | undefined>(undefined);

        useEffect(() => {
            if (data?.downtimeSummary) {
                const downtimeSummaryPerFunctionalLocationArray: IDowntimeSummaryByFunctionalLocationTotal[] =
                    data.downtimeSummary
                        .filter((summary) => summary.total_seconds > 0)
                        .map((item) => ({
                            functional_location_id: item.functional_location_id,
                            translation: item.functional_location.translation,
                            summary: {
                                aggregate: {
                                    sum: {
                                        seconds: item.total_seconds,
                                        occurrences: item.total_occurrences,
                                    },
                                },
                            },
                        }));
                setDowntimeSummaryPerFunctionalLocation(downtimeSummaryPerFunctionalLocationArray);
            }
            if (error) {
                setApolloQueryError(error);
            }
        }, [data, error]);

        return {
            loading,
            downtimeSummaryPerFunctionalLocation,
            apolloQueryError,
            refetch,
        };
    },
    Get13WeekTrendDowntimeTotals: (mill_id: number, filters: I13WeekTrendFilters) => {
        const { loading, error, data, refetch } = useQuery(
            filters.secondaryFailureCode
                ? get13WeekDowntimeSummaryTotalsBySecondaryFailureCode
                : get13WeekDowntimeSummaryTotalsByFailureCode,
            {
                variables: {
                    input_date: filters.endDate,
                    where: {
                        _or: [
                            {
                                failure_code: {
                                    _or: [
                                        { code: { _neq: END_OF_SHIFT_FAILURE_CODE_NAME } },
                                        { code: { _is_null: true } },
                                    ],
                                },
                            },
                            { failure_code_id: { _is_null: true } },
                        ],
                        ...(filters.failureType &&
                            filters.failureType !== allElements && {
                                failure_type_id: {
                                    _eq: filters.failureType,
                                },
                            }),
                        ...(filters.failureCode &&
                            filters.failureCode !== allElements && {
                                failure_code_id: {
                                    _eq: filters.failureCode,
                                },
                            }),
                        ...(filters.secondaryFailureCode &&
                            filters.secondaryFailureCode !== allElements && {
                                secondary_failure_code_id: {
                                    _eq: filters.secondaryFailureCode,
                                },
                            }),
                        machine_center: {
                            ...(filters.machineCenter &&
                                filters.machineCenter !== allElements && {
                                    machine_center_id: {
                                        _eq: filters.machineCenter,
                                    },
                                }),
                            production_line: {
                                ...(filters.productionLine &&
                                    filters.productionLine !== allElements && {
                                        production_line_id: {
                                            _eq: filters.productionLine,
                                        },
                                    }),
                                mill_id: {
                                    _eq: mill_id,
                                },
                            },
                        },
                    },
                },
            },
        );
        const [downtimes13WeekTrend, setDowntimes13WeekTrend] = useState<I13WeekTrendDowntimeTotals[]>([]);
        const [apolloQueryError, setApolloQueryError] = useState<ApolloError | undefined>(undefined);

        useEffect(() => {
            if (data?.weekly_downtime) {
                const downtimes13WeekTrendArray: I13WeekTrendDowntimeTotals[] = data.weekly_downtime;
                setDowntimes13WeekTrend(downtimes13WeekTrendArray);
            }
            if (error) {
                setApolloQueryError(error);
            }
        }, [data, error]);

        return { loading, downtimes13WeekTrend, apolloQueryError, refetch };
    },
    UpdateMillClusterFeature: () => {
        const [updateMillClusterFeature, { error, loading }] = useMutation(updateMillFeatureCluster);

        const activateClusterFeature = useCallback(
            async (millId: number) =>
                await updateMillClusterFeature({
                    variables: {
                        mill_id: millId,
                        cluster_enabled: true,
                    },
                }),
            [updateMillClusterFeature],
        );

        const deactivateClusterFeature = useCallback(
            async (millId: number) =>
                await updateMillClusterFeature({
                    variables: {
                        mill_id: millId,
                        cluster_enabled: false,
                    },
                }),
            [updateMillClusterFeature],
        );

        return { error, activateClusterFeature, deactivateClusterFeature, loading };
    },
    UpdateMillDedicatedLocalserver: () => {
        const [updateMillDedicatedLocalserverMutation, { error, loading }] = useMutation(updateMillDedicatedLocalserver);
        const updateDedicatedLocalserver = useCallback(
            async (millId: number, dedicatedLocalserver: boolean) =>
                await updateMillDedicatedLocalserverMutation({
                    variables: { mill_id: millId, dedicated_localserver: dedicatedLocalserver },
                }),
            [updateMillDedicatedLocalserverMutation],
        );
        return { error, updateDedicatedLocalserver, loading };
    },
    UpsertMillModuleAspenPlus: () => {
        const [upsertMillModuleMutation, { error, loading }] = useMutation(upsertMillModule);
        const ASPEN_PLUS_MODULE_TYPE_ID = 3;
        const setAspenPlusEnabled = useCallback(
            async (millId: number, isEnabled: boolean) =>
                await upsertMillModuleMutation({
                    variables: {
                        mill_id: millId,
                        module_type_id: ASPEN_PLUS_MODULE_TYPE_ID,
                        is_enabled: isEnabled,
                    },
                }),
            [upsertMillModuleMutation],
        );
        const activateAspenPlus = useCallback(
            (millId: number) => setAspenPlusEnabled(millId, true),
            [setAspenPlusEnabled],
        );
        const deactivateAspenPlus = useCallback(
            (millId: number) => setAspenPlusEnabled(millId, false),
            [setAspenPlusEnabled],
        );
        return { error, activateAspenPlus, deactivateAspenPlus, loading };
    },
};
